import {
  DocumentData,
  QueryDocumentSnapshot,
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import Failure from "../../../core/Failure";
import { FirestoreCollections, firestore } from "../../../firebase.config";
import NumberOptions from "../../types/NumberOptions";
import { INumber } from "@leedpulse-monorepo/interfaces";

async function getNumbers(
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  options?: NumberOptions
): Promise<{
  numbers: INumber[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
  availabilityCounts?: {
    available: number,
    assigned: number
  }
}> {
  try {
    const db = firestore;
    const numbersCollection = collection(db, FirestoreCollections.numbers);

    const numbersConstraints = [];
    const excludedCountConstraints = [];

    // Add availability option
    if (options?.available !== undefined) {
      excludedCountConstraints.push(where('available', '==', options.available));
    }

    // Add channel option
    if (options?.channel) {
      numbersConstraints.push(where('channel', '==', options.channel));
    }

    // Add product option
    if (options?.product) {
      numbersConstraints.push(where('product', '==', options.product));
    }

    const results = lastVisible
      ? await getDocs(
        query(
          numbersCollection,
          ...excludedCountConstraints,
          ...numbersConstraints,
          orderBy("created", 'desc'),
          startAfter(lastVisible),
          limit(offset)
        )
      )
      : await getDocs(
        query(
          numbersCollection,
          ...excludedCountConstraints,
          ...numbersConstraints,
          orderBy("created", 'desc'),
          limit(offset))
      );

    const numbers: INumber[] = [];
    results.docs.forEach((doc) => {
      numbers.push(doc.data() as INumber);
    });

    const numbersSnapshot = await getCountFromServer(numbersCollection);

    if (options) {
      const availableNumbersSnapshot = await getCountFromServer(
        query(
          numbersCollection,
          ...numbersConstraints,
          where('available', '==', true)
        )
      );

      const assignedNumbersSnapshot = await getCountFromServer(
        query(
          numbersCollection,
          ...numbersConstraints,
          where('available', '==', false)
        )
      );

      return {
        numbers,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: numbersSnapshot.data().count ?? 0,
        availabilityCounts: {
          available: availableNumbersSnapshot.data().count ?? 0,
          assigned: assignedNumbersSnapshot.data().count ?? 0
        }
      }
    }

    return {
      numbers,
      lastVisible: results.docs[results.docs.length - 1],
      totalCollectionCount: numbersSnapshot.data().count ?? 0,
    };
  } catch (e) {
    console.log("error: ", e);
    throw new GetNumbersFailure();
  }
}

export class GetNumbersFailure extends Failure {
  constructor(message = "Unknown error", code = "unknown-error") {
    super(message, code);
  }
}

export default getNumbers;
